import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Box, RadioGroup, FormControlLabel, Radio, FormControl } from "@mui/material";
import { UIContext } from "../../../providers/UIProvider";
import { useData } from "../../../providers/DataProvider";
import AxiosInstance from "../../../utils/axiosInstance";
import {
  DateFilterGroup,
  loadFilterOptions as loadFilterOptionsUtil,
} from "../../../components/PayrollManagementBase/PayrollManagementBase";
import { CompanyProps, RegionProps } from "../../../providers/types";

// ═══════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════

type SortBy =
  | "JobId"
  | "ProjectAndJobNumber"
  | "RegionName"
  | "CompanyName"
  | "CommunityName"
  | "JobAddress"
  | "BonusTypeDate"
  | "OpsApprovedBy"
  | "OpsApprovedOn";

type SortDirection = "Asc" | "Desc";

type PayrollCycleStatus = "AllJobs" | "IncludedInPayroll" | "PayrollSkipped";

type DateFilterType = "none" | "dateRange" | "before" | "after" | "month";

interface DateFilter {
  filterType: "None" | "Exact" | "Between" | "Before" | "After" | "Month";
  start?: string;
  end?: string;
}

interface SearchFilters {
  payrollCycleStatus: PayrollCycleStatus;
  bonusTypeId: 1 | 2;
  companyIds: number[];
  regionIds: number[];
  projectNumbers: string[];
  jobAddress: string;
  bonusTypeDate?: DateFilter;
  opsApprovedDate?: DateFilter;
}

interface SearchRequest {
  pagination: { pageNumber: number; pageSize: number };
  sorting: { sortBy: SortBy; sortDirection: SortDirection };
  filters: SearchFilters;
}

interface LookupDto {
  id: number;
  name: string;
}

interface PaySkipCycleJob {
  jobId: number;
  isPayrollCycleSkipped: boolean;
  region: LookupDto;
  company: LookupDto;
  communityName: string;
  projectAndJobNumber: string;
  jobAddress: string;
  bonusTypeDate?: string;
  opsApprovedBy: string;
  opsApprovedOn?: string;
}

interface PaginationInfo {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

interface SearchResponse {
  pagination: PaginationInfo;
  data: PaySkipCycleJob[];
}

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function buildDateFilter(
  filterType: DateFilterType,
  month: string,
  start: string,
  end: string,
  before: string,
  after: string
): DateFilter | undefined {
  switch (filterType) {
    case "month": return { filterType: "Month", start: month };
    case "dateRange": return { filterType: "Between", start, end };
    case "before": return { filterType: "Before", start: before };
    case "after": return { filterType: "After", start: after };
    default: return undefined;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Active Filter Summary Bar
// Shown above the results table/cards so user always knows what's active
// ═══════════════════════════════════════════════════════════════════════

interface FilterSummaryProps {
  filters: SearchFilters;
  companyOptions: { id: number; label: string }[];
  regionOptions: { id: number; label: string }[];
  bonusTypeId: 1 | 2;
  onClear: () => void;
  stats: {
    total: number;
    pageNumber: number;
    totalPages: number;
    included: number;
    skipped: number;
    selected: number;
    withComments: number;
  };
  loading: boolean;
  bonusTypeDateLabel: string;
}

function FilterSummaryBar({ filters, companyOptions, regionOptions, bonusTypeId, onClear, stats, loading, bonusTypeDateLabel }: FilterSummaryProps) {
  const pills: { key: string; label: string }[] = [];

  pills.push({ key: "bonusType", label: bonusTypeId === 1 ? "Stage 5 Bonus" : "Closing Bonus" });

  if (filters.payrollCycleStatus !== "AllJobs") {
    pills.push({
      key: "status",
      label: filters.payrollCycleStatus === "IncludedInPayroll" ? "Included" : "Skipped",
    });
  }
  if (filters.companyIds.length) {
    const names = filters.companyIds
      .map((id) => companyOptions.find((c) => c.id === id)?.label ?? `#${id}`)
      .join(", ");
    pills.push({ key: "companies", label: `Company: ${names}` });
  }
  if (filters.regionIds.length) {
    const names = filters.regionIds
      .map((id) => regionOptions.find((r) => r.id === id)?.label ?? `#${id}`)
      .join(", ");
    pills.push({ key: "regions", label: `Region: ${names}` });
  }
  if (filters.projectNumbers.length) {
    pills.push({ key: "projects", label: `Projects: ${filters.projectNumbers.join(", ")}` });
  }
  if (filters.jobAddress?.trim()) {
    pills.push({ key: "address", label: `Address: ${filters.jobAddress.trim()}` });
  }
  if (filters.bonusTypeDate) {
    pills.push({ key: "bonusTypeDate", label: `${bonusTypeDateLabel} filter active` });
  }
  if (filters.opsApprovedDate) {
    pills.push({ key: "opsDate", label: `Ops date filter active` });
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs min-h-[36px]">
      {/* Left — filter pills */}
      <span className="text-blue-600 font-semibold shrink-0">Showing:</span>
      {pills.map((p) => (
        <span
          key={p.key}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white border border-blue-200 text-blue-800 font-medium"
        >
          {p.label}
        </span>
      ))}
      {pills.length > 1 && (
        <button
          onClick={onClear}
          className="text-xs text-blue-500 hover:text-blue-700 underline shrink-0"
        >
          Clear filters
        </button>
      )}

      {/* Right — result stats, hidden while loading */}
      {!loading && stats.total > 0 && (
        <div className="ml-auto flex items-center gap-x-4 shrink-0 text-gray-500 pl-3 border-l border-blue-200">
          <span>
            Page: <strong className="text-gray-700">{stats.pageNumber}</strong>
            <span className="text-gray-400"> / {stats.totalPages}</span>
          </span>
          <span>Total: <strong className="text-gray-700">{stats.total}</strong></span>
          <span>Included: <strong className="text-green-700">{stats.included}</strong></span>
          <span>Skipped: <strong className="text-amber-700">{stats.skipped}</strong></span>
          {stats.selected > 0 && (
            <>
              <span className="border-l border-blue-200 pl-3">
                Selected: <strong className="text-blue-700">{stats.selected}</strong>
              </span>
              {stats.withComments > 0 && (
                <span>Comments: <strong className="text-indigo-700">{stats.withComments}</strong></span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Multi-Select Dropdown with Typeahead
// ═══════════════════════════════════════════════════════════════════════

interface MultiSelectOption {
  id: number | string;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selected: (number | string)[];
  onChange: (selected: (number | string)[]) => void;
  placeholder?: string;
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder = "All",
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (id: number | string) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  const clearAll = () => onChange([]);

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find((o) => o.id === selected[0])?.label ?? `${selected.length} selected`
        : `${selected.length} selected`;

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors"
      >
        <span className={selected.length === 0 ? "text-gray-400" : "text-gray-900"}>
          {displayText}
        </span>
        <div className="flex items-center gap-1">
          {selected.length > 0 && (
            <span
              onClick={(e) => { e.stopPropagation(); clearAll(); }}
              className="text-gray-400 hover:text-gray-600 cursor-pointer px-1"
              title="Clear"
            >
              ×
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400">No results</p>
            ) : (
              filtered.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.id)}
                    onChange={() => toggleOption(opt.id)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-gray-700">{opt.label}</span>
                </label>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button onClick={clearAll} className="text-xs text-blue-600 hover:text-blue-700">
                Clear all ({selected.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Sortable Column Header
// ═══════════════════════════════════════════════════════════════════════

interface SortableHeaderProps {
  label: string;
  sortKey: SortBy;
  currentSort: SortBy;
  currentDir: SortDirection;
  onSort: (key: SortBy) => void;
  className?: string;
}

function SortableHeader({ label, sortKey, currentSort, currentDir, onSort, className = "" }: SortableHeaderProps) {
  const active = currentSort === sortKey;
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap hover:bg-gray-300 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="flex flex-col leading-none">
          <svg className={`w-2.5 h-2.5 ${active && currentDir === "Asc" ? "text-blue-600" : "text-gray-400"}`} viewBox="0 0 10 6" fill="currentColor">
            <path d="M5 0L10 6H0L5 0Z" />
          </svg>
          <svg className={`w-2.5 h-2.5 ${active && currentDir === "Desc" ? "text-blue-600" : "text-gray-400"}`} viewBox="0 0 10 6" fill="currentColor">
            <path d="M5 6L0 0H10L5 6Z" />
          </svg>
        </span>
      </div>
    </th>
  );
}

// ── Group section — MUST be top-level (not inline) to prevent remount on every keystroke ──
interface GroupSectionProps {
  groupEntries: JobCommentEntry[];
  action: "skip" | "unskip";
  bulkText: string;
  setBulkText: (v: string) => void;
  attemptedSubmit: boolean;
  onApplyClick: (action: "skip" | "unskip") => void;
  onChangeComment: (jobId: number, comment: string) => void;
  badgeBg: string; badgeText: string; badgeDot: string; badgeLabel: string;
  borderColor: string; bgColor: string;
  bonusTypeDateLabel: string;
}

function GroupSection({
  groupEntries, action, bulkText, setBulkText,
  attemptedSubmit, onApplyClick, onChangeComment,
  badgeBg, badgeText, badgeDot, badgeLabel,
  borderColor, bgColor, bonusTypeDateLabel,
}: GroupSectionProps) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badgeBg} ${badgeText}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${badgeDot}`} />
          {badgeLabel} — {groupEntries.length} job{groupEntries.length > 1 ? "s" : ""}
        </span>
      </div>

      {groupEntries.length > 1 && (
        <div className="flex gap-2 items-center mb-3">
          <TextField
            fullWidth
            size="small"
            label="Apply comment to group"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder="Type a comment to fill/append/overwrite all jobs…"
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!bulkText.trim()}
            onClick={() => onApplyClick(action)}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Apply
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {groupEntries.map((entry) => {
          const missing = attemptedSubmit && !entry.comment.trim();
          return (
            <div
              key={entry.job.jobId}
              className={`p-3 rounded-lg border ${missing ? "border-red-400 bg-red-50" : `${borderColor} ${bgColor}`}`}
            >
              <p className="text-sm text-gray-900 mb-0.5">
                {entry.job.projectAndJobNumber || `Job #${entry.job.jobId}`}
              </p>
              <p className="text-xs text-gray-500 mb-1">
                {entry.job.company.name}{entry.job.communityName ? ` · ${entry.job.communityName}` : ""}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mb-2">
                {entry.job.jobAddress && <span>📍 {entry.job.jobAddress}</span>}
                {entry.job.bonusTypeDate && <span>📅 {bonusTypeDateLabel}: {formatDate(entry.job.bonusTypeDate)}</span>}
                {(entry.job.opsApprovedBy || entry.job.opsApprovedOn) && (
                  <span>✅ Ops: {[entry.job.opsApprovedBy, formatDate(entry.job.opsApprovedOn)].filter(Boolean).join(" · ")}</span>
                )}
              </div>
              <TextField
                fullWidth
                size="small"
                label="Comment"
                required
                error={missing}
                helperText={missing ? "Comment is required" : ""}
                value={entry.comment}
                onChange={(e) => onChangeComment(entry.job.jobId, e.target.value)}
                placeholder="Enter reason for this change…"
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Per-Job Comment Confirm Dialog
// ═══════════════════════════════════════════════════════════════════════

interface JobCommentEntry {
  job: PaySkipCycleJob;
  action: "skip" | "unskip";
  comment: string;
}

type ApplyMode = "blanks" | "all" | "append";

interface SkipCycleConfirmDialogProps {
  open: boolean;
  entries: JobCommentEntry[];
  onChangeComment: (jobId: number, comment: string) => void;
  onApplyToGroup: (action: "skip" | "unskip", comment: string, mode: ApplyMode) => void;
  onConfirm: () => void;
  onCancel: () => void;
  submitting: boolean;
  bonusTypeDateLabel: string;
}

interface ApplyModeDialogProps {
  open: boolean;
  pendingText: string;
  onChoose: (mode: ApplyMode) => void;
  onCancel: () => void;
}

function ApplyModeDialog({ open, pendingText, onChoose, onCancel }: ApplyModeDialogProps) {
  const [mode, setMode] = useState<ApplyMode>("blanks");

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth sx={{ zIndex: 1400 }}>
      <DialogTitle sx={{ pb: 1, fontSize: "0.95rem", fontWeight: 600 }}>
        Apply bulk comment
      </DialogTitle>
      <DialogContent>
        <p className="text-xs text-gray-500 mb-3 leading-relaxed">
          Comment to apply: <span className="font-medium text-gray-800">"{pendingText}"</span>
        </p>
        <p className="text-xs text-gray-500 mb-3">
          Some jobs already have comments. Choose how to apply:
        </p>
        <FormControl component="fieldset">
          <RadioGroup value={mode} onChange={(_, v) => setMode(v as ApplyMode)}>
            <FormControlLabel
              value="blanks"
              control={<Radio size="small" />}
              label={<span className="text-sm">Fill blank comments only</span>}
            />
            <FormControlLabel
              value="append"
              control={<Radio size="small" />}
              label={<span className="text-sm">Append to existing comments <span className="text-xs text-gray-400">(joins with " | ")</span></span>}
            />
            <FormControlLabel
              value="all"
              control={<Radio size="small" color="warning" />}
              label={<span className="text-sm text-amber-700">Overwrite all comments</span>}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" size="small" onClick={onCancel}>Cancel</Button>
        <Button variant="contained" size="small" onClick={() => onChoose(mode)}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}

function SkipCycleConfirmDialog({
  open, entries, onChangeComment, onApplyToGroup, onConfirm, onCancel, submitting, bonusTypeDateLabel,
}: SkipCycleConfirmDialogProps) {
  const [bulkSkip, setBulkSkip] = useState("");
  const [bulkUnskip, setBulkUnskip] = useState("");
  const [modeDialogFor, setModeDialogFor] = useState<"skip" | "unskip" | null>(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const skipEntries = entries.filter((e) => e.action === "skip");
  const unskipEntries = entries.filter((e) => e.action === "unskip");
  const allCommentsFilled = entries.every((e) => e.comment.trim().length > 0);
  const missingCount = entries.filter((e) => !e.comment.trim()).length;
  const skipHasSomeWithComment = skipEntries.some((e) => e.comment.trim().length > 0);
  const unskipHasSomeWithComment = unskipEntries.some((e) => e.comment.trim().length > 0);

  const handleApplyClick = (action: "skip" | "unskip") => {
    const text = action === "skip" ? bulkSkip : bulkUnskip;
    if (!text.trim()) return;
    const hasPartial = action === "skip" ? skipHasSomeWithComment : unskipHasSomeWithComment;
    if (hasPartial) {
      setModeDialogFor(action);
    } else {
      onApplyToGroup(action, text, "blanks");
      action === "skip" ? setBulkSkip("") : setBulkUnskip("");
    }
  };

  const handleModeChosen = (mode: ApplyMode) => {
    if (!modeDialogFor) return;
    const text = modeDialogFor === "skip" ? bulkSkip : bulkUnskip;
    onApplyToGroup(modeDialogFor, text, mode);
    modeDialogFor === "skip" ? setBulkSkip("") : setBulkUnskip("");
    setModeDialogFor(null);
  };

  const handleConfirmClick = () => {
    setAttemptedSubmit(true);
    if (!allCommentsFilled) return;
    onConfirm();
  };

  const pendingBulkText = modeDialogFor === "skip" ? bulkSkip : modeDialogFor === "unskip" ? bulkUnskip : "";

  return (
    <>
      <Dialog
        open={open}
        onClose={onCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", maxHeight: "90vh" } }}
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-blue-50 flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-blue-950">CBA — Confirm Payroll Cycle Changes</h3>
            <p className="text-xs text-blue-700 mt-0.5">A comment is required for each job before confirming.</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <DialogContent sx={{ px: 3, py: 2 }} dividers>
          <div className="space-y-6">
            {skipEntries.length > 0 && (
              <GroupSection
                groupEntries={skipEntries} action="skip"
                bulkText={bulkSkip} setBulkText={setBulkSkip}
                attemptedSubmit={attemptedSubmit}
                onApplyClick={handleApplyClick}
                onChangeComment={onChangeComment}
                badgeBg="bg-amber-100" badgeText="text-amber-800" badgeDot="bg-amber-500"
                badgeLabel="Skip Payroll Cycle"
                borderColor="border-amber-200" bgColor="bg-amber-50"
                bonusTypeDateLabel={bonusTypeDateLabel}
              />
            )}
            {unskipEntries.length > 0 && (
              <GroupSection
                groupEntries={unskipEntries} action="unskip"
                bulkText={bulkUnskip} setBulkText={setBulkUnskip}
                attemptedSubmit={attemptedSubmit}
                onApplyClick={handleApplyClick}
                onChangeComment={onChangeComment}
                badgeBg="bg-green-100" badgeText="text-green-800" badgeDot="bg-green-500"
                badgeLabel="Include in Payroll"
                borderColor="border-green-200" bgColor="bg-green-50"
                bonusTypeDateLabel={bonusTypeDateLabel}
              />
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <p className="text-sm text-gray-500">
            {entries.length} job{entries.length > 1 ? "s" : ""} will be updated
            {attemptedSubmit && missingCount > 0 && (
              <span className="ml-2 text-red-600 font-medium">
                · {missingCount} comment{missingCount > 1 ? "s" : ""} missing
              </span>
            )}
          </p>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={onCancel} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleConfirmClick}
              disabled={submitting}
              startIcon={submitting ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : undefined}
            >
              Confirm Changes
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ApplyModeDialog
        open={modeDialogFor !== null}
        pendingText={pendingBulkText}
        onChoose={handleModeChosen}
        onCancel={() => setModeDialogFor(null)}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Mobile Card Component
// ═══════════════════════════════════════════════════════════════════════

interface SkipCycleCardProps {
  job: PaySkipCycleJob;
  isSelected: boolean;
  comment: string;
  onToggle: (jobId: number) => void;
  onCommentChange: (jobId: number, comment: string) => void;
  bonusTypeId: 1 | 2;
}

function SkipCycleCard({ job, isSelected, comment, onToggle, onCommentChange, bonusTypeId }: SkipCycleCardProps) {
  const isSkipped = job.isPayrollCycleSkipped;
  const bonusTypeDateLabel = bonusTypeId === 2 ? "Close Date:" : "Stage 5 Date:";

  const statusBadge = isSkipped ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Skipped
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Included in Payroll
    </span>
  );

  const cardBg = isSkipped ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200";
  const selectedRing = isSelected ? "ring-2 ring-blue-400 ring-offset-1" : "";

  return (
    <div className={`rounded-lg border p-4 shadow-sm transition-shadow hover:shadow-md ${cardBg} ${selectedRing}`}>
      {/* Header row — checkbox + status badge, no separate pencil toggle needed */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 gap-3">
        <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(job.jobId)}
            className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 shrink-0"
            aria-label={`Select ${job.projectAndJobNumber}`}
          />
          <div className="min-w-0">{statusBadge}</div>
        </label>
        {/* Pencil is purely a "comment exists" indicator — no toggle behavior */}
        {isSelected && comment.trim() && (
          <svg className="w-4 h-4 text-blue-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
          </svg>
        )}
      </div>

      {/* Job details */}
      <div className="space-y-1.5 text-sm">
        <div className="flex gap-1"><span className="font-medium text-gray-500 w-28 shrink-0">Region:</span><span className="text-gray-900">{job.region.name || "—"}</span></div>
        <div className="flex gap-1"><span className="font-medium text-gray-500 w-28 shrink-0">Company:</span><span className="text-gray-900">{job.company.name || "—"}</span></div>
        <div className="flex gap-1"><span className="font-medium text-gray-500 w-28 shrink-0">Proj &amp; Job #:</span><span className="text-gray-900">{job.projectAndJobNumber || `ID: ${job.jobId}`}</span></div>
        <div className="flex gap-1"><span className="font-medium text-gray-500 w-28 shrink-0">Community:</span><span className="text-gray-900">{job.communityName || "—"}</span></div>
        <div className="flex gap-1"><span className="font-medium text-gray-500 w-28 shrink-0">Address:</span><span className="text-gray-900">{job.jobAddress || "—"}</span></div>
        <div className="flex gap-1"><span className="font-medium text-gray-500 w-28 shrink-0">{bonusTypeDateLabel}</span><span className="text-gray-900">{formatDate(job.bonusTypeDate)}</span></div>
        <div className="flex gap-1">
          <span className="font-medium text-gray-500 w-28 shrink-0">Ops Approved:</span>
          <div className="flex flex-col">
            {job.opsApprovedBy && <span className="text-xs text-gray-500">{job.opsApprovedBy}</span>}
            <span className="text-gray-700">{formatDate(job.opsApprovedOn)}</span>
          </div>
        </div>
      </div>

      {/* Comment input — always visible when selected, no extra tap required */}
      {isSelected && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Comment <span className="text-red-500">*</span>
            <span className="font-normal text-gray-400 ml-1">(required before submitting)</span>
          </label>
          <input
            type="text"
            value={comment}
            onChange={(e) => onCommentChange(job.jobId, e.target.value)}
            placeholder="Enter reason for this change..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Pagination Component
// ═══════════════════════════════════════════════════════════════════════

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}

function Pagination({ pagination, onPageChange }: PaginationProps) {
  const { currentPage, totalPages, totalRecords, pageSize } = pagination;
  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalRecords);

  const pages: (number | "...")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
      <p className="text-sm text-gray-500">
        Showing <span className="font-medium text-gray-700">{start}–{end}</span> of{" "}
        <span className="font-medium text-gray-700">{totalRecords}</span> jobs
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p as number)}
              className={`w-8 h-8 rounded text-sm font-medium transition-colors ${p === currentPage ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-1.5 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Inline Comment Popover
// ═══════════════════════════════════════════════════════════════════════

interface InlineCommentPopoverProps {
  jobId: number;
  currentComment: string;
  onSave: (jobId: number, comment: string) => void;
  onClose: () => void;
}

function InlineCommentPopover({ jobId, currentComment, onSave, onClose }: InlineCommentPopoverProps) {
  const [draft, setDraft] = useState(currentComment);
  const inputRef = useRef<HTMLInputElement>(null);

  // FIX #4 — proper cleanup for setTimeout focus
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  const handleSave = () => {
    onSave(jobId, draft);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-40"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="absolute z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-72"
        style={{ top: "var(--popover-top)", left: "var(--popover-left)" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <p className="text-xs font-medium text-gray-600 mb-2">Pre-stage comment for this job</p>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") onClose();
          }}
          placeholder="Comment ..."
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════

const DEFAULT_PAGE_SIZE = 200;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200, "All"] as const;
type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];
const ALL_PAGE_SIZE = 10000;

function resolvePageSize(opt: PageSizeOption): number {
  return opt === "All" ? ALL_PAGE_SIZE : opt;
}

// ═══════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════

export default function PayrollSkipCycle() {
  const { role, bonusType } = useData();
  const [, dispatch] = useContext<any>(UIContext);

  // ─── Server data ─────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<PaySkipCycleJob[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalRecords: 0,
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
  });
  const [pageSizeOption, setPageSizeOption] = useState<PageSizeOption>(DEFAULT_PAGE_SIZE);

  // ─── Mobile infinite scroll ──────────────────────────────────────────
  const [mobileRows, setMobileRows] = useState<PaySkipCycleJob[]>([]);
  const [mobileLoadingMore, setMobileLoadingMore] = useState(false);
  const [mobilePage, setMobilePage] = useState(1);
  const [mobileHasMore, setMobileHasMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filterOptions, setFilterOptions] = useState<{
    companies: CompanyProps[];
    regions: RegionProps[];
  }>({ companies: [], regions: [] });

  const defaultBonusTypeId = useMemo(
    () => (bonusType === "Stage5Bonus" ? 1 : 2) as 1 | 2,
    [bonusType]
  );

  // ─── Draft filter state ───────────────────────────────────────────────
  const [payrollCycleStatus, setPayrollCycleStatus] = useState<PayrollCycleStatus>("AllJobs");
  const [bonusTypeId, setBonusTypeId] = useState<1 | 2>(defaultBonusTypeId);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState<number[]>([]);
  const [selectedCommunityProjectNumbers, setSelectedCommunityProjectNumbers] = useState<string[]>([]);
  const [jobAddress, setJobAddress] = useState("");

  // FIX #5 — use DateFilterType union for all date filter type state
  const [bonusTypeDateFilterType, setBonusTypeDateFilterType] = useState<DateFilterType>("none");
  const [bonusTypeDateMonth, setBonusTypeDateMonth] = useState("");
  const [bonusTypeDateStart, setBonusTypeDateStart] = useState("");
  const [bonusTypeDateEnd, setBonusTypeDateEnd] = useState("");
  const [bonusTypeDateBefore, setBonusTypeDateBefore] = useState("");
  const [bonusTypeDateAfter, setBonusTypeDateAfter] = useState("");

  const [opsDateFilterType, setOpsDateFilterType] = useState<DateFilterType>("none");
  const [opsDateMonth, setOpsDateMonth] = useState("");
  const [opsDateStart, setOpsDateStart] = useState("");
  const [opsDateEnd, setOpsDateEnd] = useState("");
  const [opsDateBefore, setOpsDateBefore] = useState("");
  const [opsDateAfter, setOpsDateAfter] = useState("");

  // ─── Committed filter snapshot ────────────────────────────────────────
  const [committedFilters, setCommittedFilters] = useState<SearchFilters>({
    payrollCycleStatus: "AllJobs",
    bonusTypeId: defaultBonusTypeId,
    companyIds: [],
    regionIds: [],
    projectNumbers: [],
    jobAddress: "",
  });

  // ─── Sorting ──────────────────────────────────────────────────────────
  const [sortBy, setSortBy] = useState<SortBy>("OpsApprovedOn");
  const [sortDirection, setSortDirection] = useState<SortDirection>("Desc");

  // ─── Selection & comments ─────────────────────────────────────────────
  const [selectedJobs, setSelectedJobs] = useState<Set<number>>(new Set());
  const [jobComments, setJobComments] = useState<Map<number, string>>(new Map());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [popoverJobId, setPopoverJobId] = useState<number | null>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

  // FIX #6 — memoize selected-with-comments count used in multiple places
  const selectedWithCommentsCount = useMemo(
    () => Array.from(selectedJobs).filter((id) => jobComments.get(id)?.trim()).length,
    [selectedJobs, jobComments]
  );

  // ─── Filter option lists ──────────────────────────────────────────────
  const companyOptions = useMemo<MultiSelectOption[]>(
    () => filterOptions.companies.map((c) => ({ id: c.companyId, label: c.companyName })),
    [filterOptions.companies]
  );
  const regionOptions = useMemo<MultiSelectOption[]>(
    () => filterOptions.regions.filter((r) => r.regionId > 0).map((r) => ({ id: r.regionId, label: r.regionName })),
    [filterOptions.regions]
  );

  const communityOptions = useMemo<MultiSelectOption[]>(() => {
    const seen = new Set<string>();
    const opts: MultiSelectOption[] = [];
    [...rows, ...mobileRows].forEach((r) => {
      if (r.communityName && !seen.has(r.communityName)) {
        const projectNumber = r.projectAndJobNumber?.split("-")[0] ?? "";
        if (!projectNumber) return;
        seen.add(r.communityName);
        opts.push({ id: projectNumber, label: r.communityName });
      }
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, mobileRows]);

  // ─── Build search request ─────────────────────────────────────────────
  const buildRequest = useCallback(
    (page: number, filters: SearchFilters, ps: PageSizeOption): SearchRequest => ({
      pagination: { pageNumber: page, pageSize: resolvePageSize(ps) },
      sorting: { sortBy, sortDirection },
      filters,
    }),
    [sortBy, sortDirection]
  );

  // ─── Desktop fetch ────────────────────────────────────────────────────
  const fetchPage = useCallback(
    async (page: number, filters: SearchFilters = committedFilters, ps: PageSizeOption = pageSizeOption) => {
      setLoading(true);
      try {
        const res = await AxiosInstance.post<SearchResponse>(
          "/api/payroll/skip-cycle/search",
          buildRequest(page, filters, ps),
          { headers: { roleType: role } }
        );
        if (res?.data) {
          setRows(res.data.data);
          setPagination(res.data.pagination);
        }
      } catch (err) {
        console.error("Failed to load skip cycle data:", err);
        dispatch({ type: "Snackbar", payload: { show: true, message: "Failed to load data. Please try again.", severity: "error" } });
      } finally {
        setLoading(false);
      }
    },
    [buildRequest, committedFilters, pageSizeOption, role, dispatch]
  );

  // ─── Mobile fetch (append) ────────────────────────────────────────────
  const fetchMobilePage = useCallback(
    async (page: number, append = false, filters: SearchFilters = committedFilters, ps: PageSizeOption = pageSizeOption) => {
      if (page === 1) setLoading(true);
      else setMobileLoadingMore(true);
      try {
        const res = await AxiosInstance.post<SearchResponse>(
          "/api/payroll/skip-cycle/search",
          buildRequest(page, filters, ps),
          { headers: { roleType: role } }
        );
        if (res?.data) {
          setMobileRows((prev) => (append ? [...prev, ...res.data.data] : res.data.data));
          setMobileHasMore(page < res.data.pagination.totalPages);
          setMobilePage(page);
          if (!append) setPagination(res.data.pagination);
        }
      } catch (err) {
        console.error("Failed to load skip cycle data:", err);
      } finally {
        setLoading(false);
        setMobileLoadingMore(false);
      }
    },
    [buildRequest, committedFilters, pageSizeOption, role]
  );

  // ─── Mount ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPage(1, committedFilters, pageSizeOption);
    fetchMobilePage(1, false, committedFilters, pageSizeOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Re-fetch on sort change ──────────────────────────────────────────
  useEffect(() => {
    fetchPage(1, committedFilters, pageSizeOption);
    fetchMobilePage(1, false, committedFilters, pageSizeOption);
    setSelectedJobs(new Set());
    setJobComments(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDirection]);

  // ─── Filter option loading — FIX #7: error handling added ────────────
  useEffect(() => {
    loadFilterOptionsUtil()
      .then(setFilterOptions)
      .catch((err) => {
        console.error("Failed to load filter options:", err);
        dispatch({ type: "Snackbar", payload: { show: true, message: "Could not load filter options.", severity: "warning" } });
      });
  }, [dispatch]);

  useEffect(() => { setBonusTypeId(defaultBonusTypeId); }, [defaultBonusTypeId]);

  // ─── Search ───────────────────────────────────────────────────────────
  const handleSearch = useCallback(() => {

    // Validate date filters have values if a type is selected
    const bonusTypeDateMissing = bonusTypeDateFilterType !== "none" && (() => {
      switch (bonusTypeDateFilterType) {
        case "month": return !bonusTypeDateMonth;
        case "dateRange": return !bonusTypeDateStart || !bonusTypeDateEnd;
        case "before": return !bonusTypeDateBefore;
        case "after": return !bonusTypeDateAfter;
        default: return false;
      }
    })();

    const opsDateMissing = opsDateFilterType !== "none" && (() => {
      switch (opsDateFilterType) {
        case "month": return !opsDateMonth;
        case "dateRange": return !opsDateStart || !opsDateEnd;
        case "before": return !opsDateBefore;
        case "after": return !opsDateAfter;
        default: return false;
      }
    })();

    if (bonusTypeDateMissing || opsDateMissing) {
      const fields = [
        bonusTypeDateMissing && (bonusTypeId === 1 ? "Stage 5 Date Filter" : "Close Date Filter"),
        opsDateMissing && "Ops Approved Date",
      ].filter(Boolean).join(" and ");
      dispatch({ type: "Snackbar", payload: { show: true, message: `Please enter a value for: ${fields}`, severity: "warning" } });
      return;
    }

    const newFilters: SearchFilters = {
      payrollCycleStatus,
      bonusTypeId,
      companyIds: selectedCompanyIds,
      regionIds: selectedRegionIds,
      projectNumbers: selectedCommunityProjectNumbers,
      jobAddress: jobAddress.trim(),
      bonusTypeDate: buildDateFilter(bonusTypeDateFilterType, bonusTypeDateMonth, bonusTypeDateStart, bonusTypeDateEnd, bonusTypeDateBefore, bonusTypeDateAfter),
      opsApprovedDate: buildDateFilter(opsDateFilterType, opsDateMonth, opsDateStart, opsDateEnd, opsDateBefore, opsDateAfter),
    };
    setCommittedFilters(newFilters);
    setSelectedJobs(new Set());
    setJobComments(new Map());
    fetchPage(1, newFilters, pageSizeOption);
    fetchMobilePage(1, false, newFilters, pageSizeOption);
  }, [
    payrollCycleStatus, bonusTypeId, selectedCompanyIds, selectedRegionIds,
    selectedCommunityProjectNumbers, jobAddress,
    bonusTypeDateFilterType, bonusTypeDateMonth, bonusTypeDateStart, bonusTypeDateEnd, bonusTypeDateBefore, bonusTypeDateAfter,
    opsDateFilterType, opsDateMonth, opsDateStart, opsDateEnd, opsDateBefore, opsDateAfter,
    pageSizeOption, fetchPage, fetchMobilePage,
  ]);

  // ─── Page size ────────────────────────────────────────────────────────
  const handlePageSizeChange = useCallback((opt: PageSizeOption) => {
    setPageSizeOption(opt);
    fetchPage(1, committedFilters, opt);
    fetchMobilePage(1, false, committedFilters, opt);
    setSelectedJobs(new Set());
    setJobComments(new Map());
  }, [committedFilters, fetchPage, fetchMobilePage]);

  // ─── Infinite scroll ──────────────────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && mobileHasMore && !mobileLoadingMore)
          fetchMobilePage(mobilePage + 1, true);
      },
      { threshold: 0.5 }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [mobileHasMore, mobileLoadingMore, mobilePage, fetchMobilePage]);

  // ─── Sort ─────────────────────────────────────────────────────────────
  const handleSort = useCallback((key: SortBy) => {
    if (sortBy === key) setSortDirection((prev) => (prev === "Asc" ? "Desc" : "Asc"));
    else { setSortBy(key); setSortDirection("Asc"); }
  }, [sortBy]);

  // ─── Selection ────────────────────────────────────────────────────────
  const handleToggleJob = useCallback((jobId: number) => {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
        setJobComments((c) => { const n = new Map(c); n.delete(jobId); return n; });
      } else {
        next.add(jobId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedJobs.size === rows.length && rows.length > 0) {
      setSelectedJobs(new Set());
      setJobComments(new Map());
    } else {
      setSelectedJobs(new Set(rows.map((r) => r.jobId)));
    }
  }, [selectedJobs.size, rows]);

  const handleCommentChange = useCallback((jobId: number, comment: string) => {
    setJobComments((prev) => { const next = new Map(prev); next.set(jobId, comment); return next; });
  }, []);

  // ─── Confirm dialog entries ───────────────────────────────────────────
  const confirmEntries = useMemo((): JobCommentEntry[] => {
    const allRows = [...rows, ...mobileRows];
    const seen = new Set<number>();
    const unique = allRows.filter((r) => { if (seen.has(r.jobId)) return false; seen.add(r.jobId); return true; });
    return unique
      .filter((r) => selectedJobs.has(r.jobId))
      .map((r) => ({
        job: r,
        action: r.isPayrollCycleSkipped ? "unskip" : "skip",
        comment: jobComments.get(r.jobId) ?? "",
      }));
  }, [rows, mobileRows, selectedJobs, jobComments]);

  const handleApplyToGroup = useCallback(
    (action: "skip" | "unskip", globalComment: string, mode: ApplyMode) => {
      const entries = confirmEntries.filter((e) => e.action === action);
      setJobComments((prev) => {
        const next = new Map(prev);
        entries.forEach((e) => {
          const existing = next.get(e.job.jobId)?.trim() ?? "";
          if (mode === "blanks" && existing) return;
          if (mode === "append" && existing) {
            next.set(e.job.jobId, `${existing} | ${globalComment}`);
          } else {
            next.set(e.job.jobId, globalComment);
          }
        });
        return next;
      });
    },
    [confirmEntries]
  );

  const handleDialogCommentChange = useCallback((jobId: number, comment: string) => {
    setJobComments((prev) => { const next = new Map(prev); next.set(jobId, comment); return next; });
  }, []);

  // ─── Submit ───────────────────────────────────────────────────────────
  const handleSubmitClick = useCallback(() => {
    if (selectedJobs.size === 0) {
      dispatch({ type: "Snackbar", payload: { show: true, message: "No jobs selected.", severity: "info" } });
      return;
    }
    setConfirmDialogOpen(true);
  }, [selectedJobs.size, dispatch]);

  const handleConfirmSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const payload = confirmEntries.map((e) => ({
        jobId: e.job.jobId,
        isPayrollCycleSkipped: e.action === "skip",
        comment: e.comment.trim(),
      }));

      await AxiosInstance.post(`/api/payroll/skip-cycle/update?bonusTypeId=${bonusTypeId}`, payload, { headers: { roleType: role } });

      // FIX #8 — O(1) lookup via Map instead of O(n*m) .find() inside map
      const updatesByJobId = new Map(confirmEntries.map((e) => [e.job.jobId, e]));
      const updateRows = (prev: PaySkipCycleJob[]) =>
        prev.map((r) => {
          const entry = updatesByJobId.get(r.jobId);
          return entry ? { ...r, isPayrollCycleSkipped: entry.action === "skip" } : r;
        });

      setRows(updateRows);
      setMobileRows(updateRows);

      const skipCount = confirmEntries.filter((e) => e.action === "skip").length;
      const unskipCount = confirmEntries.filter((e) => e.action === "unskip").length;
      const parts = [];
      if (skipCount) parts.push(`${skipCount} skipped`);
      if (unskipCount) parts.push(`${unskipCount} included`);

      dispatch({ type: "Snackbar", payload: { show: true, message: `Successfully updated: ${parts.join(", ")}.`, severity: "success" } });
      setSelectedJobs(new Set());
      setJobComments(new Map());
    } catch (err) {
      console.error("Failed to submit skip cycle changes:", err);
      dispatch({ type: "Snackbar", payload: { show: true, message: "Failed to submit changes. Please try again.", severity: "error" } });
    } finally {
      setSubmitting(false);
      setConfirmDialogOpen(false);
    }
  }, [confirmEntries, role, dispatch]);

  // ─── Clear filters ────────────────────────────────────────────────────
  const clearFilters = useCallback(() => {
    const resetFilters: SearchFilters = {
      payrollCycleStatus: "AllJobs",
      bonusTypeId: defaultBonusTypeId,
      companyIds: [], regionIds: [], projectNumbers: [], jobAddress: "",
    };
    setPayrollCycleStatus("AllJobs");
    setBonusTypeId(defaultBonusTypeId);
    setSelectedCompanyIds([]);
    setSelectedRegionIds([]);
    setSelectedCommunityProjectNumbers([]);
    setJobAddress("");
    setBonusTypeDateFilterType("none");
    setBonusTypeDateMonth(""); setBonusTypeDateStart(""); setBonusTypeDateEnd(""); setBonusTypeDateBefore(""); setBonusTypeDateAfter("");
    setOpsDateFilterType("none");
    setOpsDateMonth(""); setOpsDateStart(""); setOpsDateEnd(""); setOpsDateBefore(""); setOpsDateAfter("");
    setSelectedJobs(new Set());
    setJobComments(new Map());
    setCommittedFilters(resetFilters);
    fetchPage(1, resetFilters, pageSizeOption);
    fetchMobilePage(1, false, resetFilters, pageSizeOption);
  }, [defaultBonusTypeId, pageSizeOption, fetchPage, fetchMobilePage]);

  // ─── Active filter count ──────────────────────────────────────────────
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (committedFilters.payrollCycleStatus !== "AllJobs") count++;
    if (committedFilters.companyIds.length) count++;
    if (committedFilters.regionIds.length) count++;
    if (committedFilters.projectNumbers.length) count++;
    if (committedFilters.jobAddress?.trim()) count++;
    if (committedFilters.bonusTypeDate) count++;
    if (committedFilters.opsApprovedDate) count++;
    return count;
  }, [committedFilters]);

  const allSelected = rows.length > 0 && rows.every((r) => selectedJobs.has(r.jobId));
  const bonusTypeDateColumnLabel = bonusTypeId === 2 ? "Close Date" : "Stage 5 Date";

  const LoadingSpinner = () => (
    <div className="flex flex-col items-center gap-3 py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
      <span className="text-sm text-gray-500">Loading data...</span>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <main className="flex-grow p-2 sm:p-4 lg:p-6">
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">

        {/* ─── Header ───────────────────────────────────────────────── */}
        <div className="p-3 sm:p-4 lg:p-6 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Payroll Skip Cycle Management
            </h2>

            <button
              onClick={() => setFiltersExpanded((prev) => !prev)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md relative self-start transition-colors"
            >
              {/* Static funnel icon — never rotates */}
              <svg
                className="w-4 h-4 shrink-0"
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
              </svg>
              <span>{filtersExpanded ? "Hide Filters" : "Filters"}</span>
              {/* Chevron rotates to indicate panel state */}
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${filtersExpanded ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {!filtersExpanded && activeFilterCount > 0 && (
                <span className="bg-blue-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* ─── Filter Panel ─────────────────────────────────────── */}
          <div className={`mt-3 p-3 sm:p-4 bg-white rounded-md border border-gray-300 ${filtersExpanded ? "block" : "hidden"}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

              {/* Payroll Cycle Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payroll Cycle Status</label>
                <select
                  value={payrollCycleStatus}
                  onChange={(e) => setPayrollCycleStatus(e.target.value as PayrollCycleStatus)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="AllJobs">All Jobs</option>
                  <option value="IncludedInPayroll">Included in Payroll</option>
                  <option value="PayrollSkipped">Skipped</option>
                </select>
              </div>

              {/* Bonus Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bonus Type</label>
                <select
                  value={bonusTypeId}
                  onChange={(e) => setBonusTypeId(Number(e.target.value) as 1 | 2)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>Stage 5 Bonus</option>
                  <option value={2}>Closing Bonus</option>
                </select>
              </div>

              {/* Company */}
              <MultiSelectDropdown
                label="Company"
                options={companyOptions}
                selected={selectedCompanyIds}
                onChange={(ids) => setSelectedCompanyIds(ids as number[])}
                placeholder="All Companies"
              />

              {/* Region */}
              <MultiSelectDropdown
                label="Region"
                options={regionOptions}
                selected={selectedRegionIds}
                onChange={(ids) => setSelectedRegionIds(ids as number[])}
                placeholder="All Regions"
              />

              {/* Community */}
              <MultiSelectDropdown
                label="Community"
                options={communityOptions}
                selected={selectedCommunityProjectNumbers}
                onChange={(ids) => setSelectedCommunityProjectNumbers(ids as string[])}
                placeholder="All Communities"
              />

              {/* Job Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Address</label>
                <input
                  type="text"
                  value={jobAddress}
                  onChange={(e) => setJobAddress(e.target.value)}
                  placeholder="Search by address..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Sort (mobile only) */}
              <div className="lg:hidden">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortBy)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="OpsApprovedOn">Ops Approved Date</option>
                    <option value="OpsApprovedBy">Ops Approved By</option>
                    <option value="ProjectAndJobNumber">Project &amp; Job #</option>
                    <option value="CompanyName">Company</option>
                    <option value="RegionName">Region</option>
                    <option value="CommunityName">Community</option>
                    <option value="JobAddress">Address</option>
                    <option value="BonusTypeDate">{bonusTypeDateColumnLabel}</option>
                    <option value="JobId">Job ID</option>
                  </select>
                  <button
                    onClick={() => setSortDirection((d) => (d === "Asc" ? "Desc" : "Asc"))}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                    title={sortDirection === "Asc" ? "Ascending" : "Descending"}
                  >
                    {sortDirection === "Asc" ? "↑" : "↓"}
                  </button>
                </div>
              </div>
            </div>

            {/* Date filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
              <DateFilterGroup
                label={bonusTypeId === 1 ? "Stage 5 Date Filter" : "Close Date Filter"}
                filterType={bonusTypeDateFilterType as any}
                month={bonusTypeDateMonth}
                startDate={bonusTypeDateStart}
                endDate={bonusTypeDateEnd}
                beforeDate={bonusTypeDateBefore}
                afterDate={bonusTypeDateAfter}
                onTypeChange={(v) => setBonusTypeDateFilterType(v as DateFilterType)}
                onMonthChange={setBonusTypeDateMonth}
                onStartChange={setBonusTypeDateStart}
                onEndChange={setBonusTypeDateEnd}
                onBeforeChange={setBonusTypeDateBefore}
                onAfterChange={setBonusTypeDateAfter}
              />
              <DateFilterGroup
                label="Ops Approved Date"
                filterType={opsDateFilterType as any}
                month={opsDateMonth}
                startDate={opsDateStart}
                endDate={opsDateEnd}
                beforeDate={opsDateBefore}
                afterDate={opsDateAfter}
                onTypeChange={(v) => setOpsDateFilterType(v as DateFilterType)}
                onMonthChange={setOpsDateMonth}
                onStartChange={setOpsDateStart}
                onEndChange={setOpsDateEnd}
                onBeforeChange={setOpsDateBefore}
                onAfterChange={setOpsDateAfter}
              />
            </div>

            {/* Filter actions row */}
            <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-100 items-center">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                )}
                Search
              </button>
              <button
                onClick={clearFilters}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Clear Filters
              </button>
              {activeFilterCount > 0 && (
                <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full">
                  {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} active
                </span>
              )}
              {/* Page Size */}
              <div className="ml-auto flex items-center gap-2">
                <label className="text-sm text-gray-500 whitespace-nowrap">Rows per page:</label>
                <select
                  value={pageSizeOption}
                  onChange={(e) => {
                    const val = e.target.value === "All" ? "All" : (Number(e.target.value) as PageSizeOption);
                    handlePageSizeChange(val as PageSizeOption);
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 w-24 focus:ring-blue-500 bg-white"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ─── Submit button ────────────────────────────────────── */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              onClick={handleSubmitClick}
              disabled={loading || selectedJobs.size === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Submit Changes ({selectedJobs.size})
            </button>
            {selectedJobs.size > 0 && (
              <span className="text-sm text-gray-500">
                <span className="font-semibold text-blue-700">{selectedJobs.size}</span>{" "}
                job{selectedJobs.size > 1 ? "s" : ""} selected
                {selectedWithCommentsCount > 0 && (
                  <span className="ml-2 text-indigo-600">
                    · {selectedWithCommentsCount} with comments
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Legend */}
          <details className="mt-3">
            <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800 select-none">
              Legend &amp; Color Key
            </summary>
            <div className="mt-2 p-3 bg-white rounded-md border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 w-5 h-5 rounded border border-green-300 bg-green-50" />
                  <span>Included in payroll — will appear in the next payroll run</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="shrink-0 w-5 h-5 rounded border border-amber-300 bg-amber-50" />
                  <span>Temporarily skipped — excluded from current cycle, can be re-included</span>
                </div>
                <div className="flex items-start gap-2 sm:col-span-2 text-gray-500">
                  <span>
                    Select jobs via checkboxes, then click <strong className="text-gray-700">Submit Changes</strong>.
                    Click the{" "}
                    <span className="inline-flex items-center gap-1 align-middle">
                      <svg className="w-3.5 h-3.5 text-gray-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <strong className="text-gray-700">pencil</strong>
                    </span>{" "}
                    icon (appears when a row is selected) to pre-stage a comment — required before submitting.
                  </span>
                </div>
              </div>
            </div>
          </details>
        </div>

        {/* ─── Active Filter Summary Bar ─────────────────────────────
                    Always visible above results so users know what's being fetched,
                    even when the filter panel is collapsed.
                ─────────────────────────────────────────────────────────────── */}
        <FilterSummaryBar
          filters={committedFilters}
          companyOptions={companyOptions as { id: number; label: string }[]}
          regionOptions={regionOptions as { id: number; label: string }[]}
          bonusTypeId={committedFilters.bonusTypeId}
          onClear={clearFilters}
          loading={loading}
          stats={{
            total: pagination.totalRecords,
            pageNumber: pagination.currentPage,
            totalPages: pagination.totalPages,
            included: rows.filter((r) => !r.isPayrollCycleSkipped).length,
            skipped: rows.filter((r) => r.isPayrollCycleSkipped).length,
            selected: selectedJobs.size,
            withComments: selectedWithCommentsCount,
          }}
          bonusTypeDateLabel={committedFilters.bonusTypeId === 1 ? "Stage 5 Date" : "Close Date"}
        />

        {/* ─── Mobile Card View (< lg) ───────────────────────────── */}
        <div className="lg:hidden">
          {loading ? (
            <LoadingSpinner />
          ) : mobileRows.length > 0 ? (
            <>
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedJobs.size === mobileRows.length && mobileRows.length > 0}
                    onChange={() => {
                      if (selectedJobs.size === mobileRows.length) { setSelectedJobs(new Set()); setJobComments(new Map()); }
                      else setSelectedJobs(new Set(mobileRows.map((r) => r.jobId)));
                    }}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-700">Select All ({mobileRows.length} loaded)</span>
                  {selectedJobs.size > 0 && (
                    <span className="ml-auto text-xs text-blue-700 font-medium">{selectedJobs.size} selected</span>
                  )}
                </label>
              </div>
              <div className="p-3 space-y-3">
                {mobileRows.map((job) => (
                  <SkipCycleCard
                    key={job.jobId}
                    job={job}
                    isSelected={selectedJobs.has(job.jobId)}
                    comment={jobComments.get(job.jobId) ?? ""}
                    onToggle={handleToggleJob}
                    onCommentChange={handleCommentChange}
                    bonusTypeId={bonusTypeId}
                  />
                ))}
              </div>
              <div ref={loadMoreRef} className="py-4 flex justify-center">
                {mobileLoadingMore ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" /> Loading more...
                  </div>
                ) : mobileHasMore ? (
                  <button onClick={() => fetchMobilePage(mobilePage + 1, true)} className="px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50">
                    Load More
                  </button>
                ) : (
                  <p className="text-sm text-gray-400">All {pagination.totalRecords} jobs loaded</p>
                )}
              </div>
            </>
          ) : (
            <div className="px-6 py-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-sm">No jobs found. Try adjusting your filters.</p>
            </div>
          )}
        </div>

        {/* ─── Desktop Table View (≥ lg) ─────────────────────────── */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-200">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                  <div className="flex flex-col items-center gap-1">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      title="Select all on this page"
                    />
                    {selectedJobs.size > 0 ? (
                      <span className="text-[10px] text-blue-700 font-medium leading-tight whitespace-nowrap">
                        {selectedJobs.size} sel.
                      </span>
                    ) : (
                      <span className="text-[10px] leading-tight text-gray-400">Select</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <SortableHeader label="Region" sortKey="RegionName" currentSort={sortBy} currentDir={sortDirection} onSort={handleSort} />
                <SortableHeader label="Company" sortKey="CompanyName" currentSort={sortBy} currentDir={sortDirection} onSort={handleSort} />
                <SortableHeader label="Project & Job #" sortKey="ProjectAndJobNumber" currentSort={sortBy} currentDir={sortDirection} onSort={handleSort} />
                <SortableHeader label="Community" sortKey="CommunityName" currentSort={sortBy} currentDir={sortDirection} onSort={handleSort} />
                <SortableHeader label="Job Address" sortKey="JobAddress" currentSort={sortBy} currentDir={sortDirection} onSort={handleSort} />
                <SortableHeader label={bonusTypeDateColumnLabel} sortKey="BonusTypeDate" currentSort={sortBy} currentDir={sortDirection} onSort={handleSort} />
                <SortableHeader label="Ops Approved" sortKey="OpsApprovedOn" currentSort={sortBy} currentDir={sortDirection} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={9} className="py-0"><LoadingSpinner /></td></tr>
              ) : rows.length > 0 ? (
                rows.map((job) => {
                  const isSelected = selectedJobs.has(job.jobId);
                  const isSkipped = job.isPayrollCycleSkipped;
                  const comment = jobComments.get(job.jobId) ?? "";

                  let rowBg = "hover:bg-gray-50";
                  if (isSkipped) rowBg = "bg-amber-50 hover:bg-amber-100";
                  if (isSelected && !isSkipped) rowBg = "bg-blue-50 hover:bg-blue-100";
                  if (isSelected && isSkipped) rowBg = "bg-amber-100 hover:bg-amber-200";

                  return (
                    <tr
                      key={job.jobId}
                      className={`transition-colors cursor-pointer ${rowBg}`}
                      onClick={() => handleToggleJob(job.jobId)}
                    >
                      <td className="px-4 py-3 text-center w-24" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleJob(job.jobId)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            aria-label={`Toggle ${job.projectAndJobNumber}`}
                          />
                          {isSelected ? (
                            <button
                              type="button"
                              title={comment ? `Comment: ${comment}` : "Add comment"}
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setPopoverPos({ top: rect.bottom + 8, left: Math.min(rect.left, window.innerWidth - 300) });
                                setPopoverJobId(job.jobId);
                              }}
                              className="p-0.5 rounded hover:bg-white/80 transition-colors"
                            >
                              {comment ? (
                                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-gray-400 hover:text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              )}
                            </button>
                          ) : (
                            <span className="w-4 h-4 inline-block" aria-hidden="true" />
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {isSkipped ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Skipped
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Included
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{job.region.name || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{job.company.name || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700" title={`Job ID: ${job.jobId}`}>
                        {job.projectAndJobNumber || `ID: ${job.jobId}`}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{job.communityName || "—"}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{job.jobAddress || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{formatDate(job.bonusTypeDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {job.opsApprovedBy || job.opsApprovedOn ? (
                          <div className="flex flex-col">
                            {job.opsApprovedBy && <span className="text-xs text-gray-500">{job.opsApprovedBy}</span>}
                            <span>{formatDate(job.opsApprovedOn)}</span>
                          </div>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9}>
                    <div className="px-6 py-12 text-center">
                      <p className="text-gray-500 text-sm">No jobs found. Try adjusting your filters.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ─── Desktop Pagination ────────────────────────────────── */}
        {!loading && pagination.totalPages > 1 && (
          <div className="hidden lg:block">
            <Pagination
              pagination={pagination}
              onPageChange={(page) => fetchPage(page, committedFilters, pageSizeOption)}
            />
          </div>
        )}

      </div>

      {/* ─── Confirm Dialog ───────────────────────────────────────── */}
      <SkipCycleConfirmDialog
        open={confirmDialogOpen}
        entries={confirmEntries}
        onChangeComment={handleDialogCommentChange}
        onApplyToGroup={handleApplyToGroup}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setConfirmDialogOpen(false)}
        submitting={submitting}
        bonusTypeDateLabel={bonusTypeId === 1 ? "Stage 5 Date" : "Close Date"}
      />

      {/* ─── Inline Comment Popover ───────────────────────────────── */}
      {popoverJobId !== null && (
        <div style={{ "--popover-top": `${popoverPos.top}px`, "--popover-left": `${popoverPos.left}px` } as React.CSSProperties}>
          <InlineCommentPopover
            jobId={popoverJobId}
            currentComment={jobComments.get(popoverJobId) ?? ""}
            onSave={handleCommentChange}
            onClose={() => setPopoverJobId(null)}
          />
        </div>
      )}
    </main>
  );
}